import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, UserPlus, Shield, Eye, User, Crown, X, Mail, Copy, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatRelative } from "@/lib/constants";
import { useConfirm } from "@/components/Confirm";

const ROLE_META = {
  owner:  { label: "Owner",  icon: Crown,  cls: "bg-amber-50  text-amber-800 border-amber-200" },
  admin:  { label: "Admin",  icon: Shield, cls: "bg-blue-50   text-blue-800 border-blue-200" },
  member: { label: "Member", icon: User,   cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  viewer: { label: "Viewer", icon: Eye,    cls: "bg-slate-50  text-slate-700 border-slate-200" },
};

const ROLE_DESCRIPTIONS = {
  admin:  "Manage everything except billing and ownership.",
  member: "Use chats, tasks, leads. Cannot edit settings.",
  viewer: "Read-only access across the workspace.",
};

export default function TeamSettings() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/account/members");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load team");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const yourRole = data?.your_role || "member";
  const canInvite = yourRole === "owner" || yourRole === "admin";
  const canManage = yourRole === "owner";

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <Link to="/app/settings" className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-900 mb-3">
        <ChevronLeft size={14} /> Settings
      </Link>

      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900 leading-tight">Team</h1>
          <p className="text-[14px] text-slate-500 mt-1">
            Invite colleagues, assign roles, manage who can see and act on this workspace.
          </p>
        </div>
        {canInvite && (
          <button
            onClick={() => setShowInvite(true)}
            className="h-10 px-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-[13px] font-semibold transition active:scale-95"
            data-testid="invite-member-button"
          >
            <UserPlus size={14} /> Invite member
          </button>
        )}
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500">Loading…</div>
      ) : !data ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-red-600">Failed to load.</div>
      ) : (
        <>
          {/* Members */}
          <section className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6">
            <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Members ({data.members.length})
              </div>
            </header>
            <ul>
              {data.members.map((m) => (
                <MemberRow
                  key={m.id || m.email}
                  m={m}
                  canManage={canManage}
                  onChangeRole={async (role) => {
                    try {
                      await api.patch(`/account/members/${m.id}/role`, { role });
                      toast.success("Role updated");
                      await load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Update failed");
                    }
                  }}
                  onRemove={async () => {
                    const ok = await confirm({
                      title: `Remove ${m.name || m.email}?`,
                      message: "They lose access to this workspace immediately. You can re-invite them later.",
                      confirmLabel: "Remove",
                      tone: "danger",
                    });
                    if (!ok) return;
                    try {
                      await api.delete(`/account/members/${m.id}`);
                      toast.success("Member removed");
                      await load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Remove failed");
                    }
                  }}
                />
              ))}
            </ul>
          </section>

          {/* Pending invites */}
          {data.invites.length > 0 && (
            <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <header className="px-5 py-3 border-b border-slate-200">
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Pending invitations ({data.invites.length})
                </div>
              </header>
              <ul>
                {data.invites.map((inv) => (
                  <InviteRow
                    key={inv.id}
                    inv={inv}
                    canRevoke={canInvite}
                    onRevoke={async () => {
                      const ok = await confirm({
                        title: "Revoke this invitation?",
                        message: "The link in their email will stop working immediately.",
                        confirmLabel: "Revoke",
                        tone: "danger",
                      });
                      if (!ok) return;
                      try {
                        await api.delete(`/account/invites/${inv.id}`);
                        toast.success("Invitation revoked");
                        await load();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Revoke failed");
                      }
                    }}
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {showInvite && (
        <InviteModal
          yourRole={yourRole}
          onClose={() => setShowInvite(false)}
          onSent={async () => { setShowInvite(false); await load(); }}
        />
      )}
    </div>
  );
}

// ── Member row ─────────────────────────────────────────────────────────────

function MemberRow({ m, canManage, onChangeRole, onRemove }) {
  const meta = ROLE_META[m.role] || ROLE_META.member;
  const Icon = meta.icon;
  const isOwner = m.role === "owner";
  const showActions = canManage && !isOwner && !m.is_you;
  const initials = (m.name || m.email || "?").split(/\s+/).map(w => w[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();

  return (
    <li className="px-5 py-4 flex items-center gap-4 border-b last:border-b-0 border-slate-100">
      <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center text-[12px] font-bold text-slate-600 shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold text-slate-900 truncate">
            {m.name || m.email || "Unknown"}
          </span>
          {m.is_you && <span className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-slate-400">You</span>}
        </div>
        <div className="text-[12px] text-slate-500 truncate">
          {m.email} {m.last_seen_at && <span className="text-slate-400 ml-2">· active {formatRelative(m.last_seen_at)}</span>}
        </div>
      </div>
      <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11.5px] font-semibold " + meta.cls}>
        <Icon size={12} /> {meta.label}
      </span>
      {showActions && (
        <div className="flex items-center gap-2 shrink-0">
          <select
            value={m.role}
            onChange={(e) => onChangeRole(e.target.value)}
            className="h-8 px-2 border border-slate-200 rounded-lg bg-white text-[12.5px] hover:border-slate-400"
            data-testid={`role-select-${m.id}`}
          >
            <option value="admin">Admin</option>
            <option value="member">Member</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            onClick={onRemove}
            title="Remove from workspace"
            className="h-8 w-8 grid place-items-center text-slate-400 hover:text-red-600 border border-slate-200 rounded-lg"
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </li>
  );
}

// ── Pending invite row ─────────────────────────────────────────────────────

function InviteRow({ inv, canRevoke, onRevoke }) {
  const meta = ROLE_META[inv.role] || ROLE_META.member;
  const Icon = meta.icon;
  return (
    <li className="px-5 py-3.5 flex items-center gap-4 border-b last:border-b-0 border-slate-100">
      <div className="w-10 h-10 rounded-full bg-amber-50 grid place-items-center text-amber-700 shrink-0">
        <Mail size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-slate-900 truncate">{inv.email}</div>
        <div className="text-[12px] text-slate-500">
          Invited {formatRelative(inv.created_at)} · expires {formatRelative(inv.expires_at)}
        </div>
      </div>
      <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11.5px] font-semibold " + meta.cls}>
        <Icon size={12} /> {meta.label}
      </span>
      {canRevoke && (
        <button
          onClick={onRevoke}
          className="h-8 px-3 inline-flex items-center gap-1.5 border border-slate-200 hover:border-red-500 hover:text-red-600 rounded-lg text-[12.5px]"
        >
          Revoke
        </button>
      )}
    </li>
  );
}

// ── Invite modal ───────────────────────────────────────────────────────────

function InviteModal({ yourRole, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("member");
  const [sending, setSending] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [emailFailed, setEmailFailed] = useState(false);

  // Owner can invite admins; admins can only invite member/viewer.
  const roleOptions = yourRole === "owner"
    ? ["admin", "member", "viewer"]
    : ["member", "viewer"];

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setShareUrl(null);
    setEmailFailed(false);
    try {
      const r = await api.post("/account/invite", {
        email: email.trim(), name: name.trim() || null, role,
      });
      if (r.data.email_status === "sent") {
        toast.success("Invitation sent");
        onSent?.();
      } else {
        setShareUrl(r.data.share_url);
        setEmailFailed(true);
        toast.warning("Email failed — share the link manually");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-[15px] font-bold text-slate-900">Invite a member</h2>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center hover:bg-slate-100 rounded-lg">
            <X size={16} />
          </button>
        </div>
        {shareUrl ? (
          <div className="px-5 py-5 space-y-3">
            <div className="text-[13px] text-slate-700">
              Email delivery failed, but the invitation was created. Share this link directly:
            </div>
            <div className="flex gap-2">
              <input
                type="text" readOnly value={shareUrl}
                className="flex-1 h-10 px-3 border border-slate-200 rounded-lg text-[12px] font-mono bg-slate-50"
              />
              <button
                onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }}
                className="h-10 px-3 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[12.5px]"
              >
                <Copy size={13} /> Copy
              </button>
            </div>
            <div className="text-right">
              <button onClick={onSent} className="h-9 px-4 text-[13px] font-semibold text-blue-600 hover:underline">
                Done
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="px-5 py-5 space-y-4">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Email</label>
              <input
                type="email" required autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-slate-900 outline-none"
                data-testid="invite-email"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Name <span className="text-slate-400 font-normal normal-case tracking-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Their name (for the email greeting)"
                className="mt-1.5 w-full h-10 px-3 border border-slate-200 rounded-lg text-[13.5px] focus:border-slate-900 outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Role</label>
              <div className="mt-2 space-y-1.5">
                {roleOptions.map((r) => {
                  const meta = ROLE_META[r];
                  const Icon = meta.icon;
                  return (
                    <label
                      key={r}
                      className={"flex items-start gap-2.5 p-2.5 border rounded-lg cursor-pointer transition " +
                        (role === r ? "border-blue-500 bg-blue-50/60" : "border-slate-200 hover:border-slate-400")}
                    >
                      <input
                        type="radio" name="role" value={r}
                        checked={role === r} onChange={() => setRole(r)}
                        className="mt-1"
                      />
                      <Icon size={14} className="mt-0.5 text-slate-500" />
                      <div className="flex-1">
                        <div className="text-[13px] font-semibold text-slate-900">{meta.label}</div>
                        <div className="text-[11.5px] text-slate-500">{ROLE_DESCRIPTIONS[r]}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="h-9 px-4 border border-slate-200 hover:border-slate-400 rounded-lg text-[13px]">
                Cancel
              </button>
              <button
                type="submit"
                disabled={sending || !email.trim()}
                className="h-9 px-4 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[13px] font-semibold disabled:opacity-50"
                data-testid="invite-send"
              >
                {sending ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                {sending ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
